import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { storeValue } from "../../../utils/Functions";
import { LoginImages } from "../../../reusableUI/images";
import secureLocalStorage from "react-secure-storage";
import { showToast } from "../../../reusableUI/toaster";
import { HospitalOTP, HospitalLog } from "../../../Service/partnerAPI";

function HospitalLogin() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [isOtpScreen, setIsOtpScreen] = useState(false);
  const [otp, setOtp] = useState("");

  const [showResend, setShowResend] = useState(false);
  const [timer, setTimer] = useState(60);

  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
  const [resendOtpLoading, setResendOtpLoading] = useState(false);

  useEffect(() => {
    let countdown;
    if (isOtpScreen && timer > 0) {
      countdown = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (isOtpScreen && timer <= 0) {
      setShowResend(true);
    }
    return () => countdown && clearInterval(countdown);
  }, [isOtpScreen, timer]);

  const isValidEmail = (value) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(value);
  };

  const maskEmail = (value) => {
    if (!value) return "";
    const [user, domain] = value.split("@");
    if (!domain) return value;
    if (user.length <= 2) return `${user[0]}***@${domain}`;
    return `${user[0]}${"*".repeat(Math.min(4, user.length - 1))}@${domain}`;
  };

  const sendOTP = async () => {
    const cleanEmail = email.trim().toLowerCase();

    if (!isValidEmail(cleanEmail)) {
      showToast("Enter a valid email address", "Error");
      return;
    }

    setSendOtpLoading(true);
    try {
      const res = await HospitalOTP({ email: cleanEmail });
      if (res?.error === false) {
        showToast("OTP sent successfully. Please check your email.", "Success");
        setIsOtpScreen(true);
        setTimer(60);
        setShowResend(false);
        setOtp("");
      } else {
        showToast(res?.message || "Failed to send OTP", "Error");
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      showToast("Failed to send OTP. Please try again.", "Error");
    } finally {
      setSendOtpLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 4) setOtp(value);
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 4) {
      showToast("Please enter a 4-digit OTP", "Error");
      return;
    }

    setVerifyOtpLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const data = { email: cleanEmail, OTP: Number(otp) };

      const _login = await HospitalLog(data);

      if (_login?.error === false) {
        showToast(_login?.message || "Login successful", "Success");

        storeValue("token", _login?.data?.token);
        storeValue("expiresIn", _login?.data?.expiresIn);
        storeValue("refreshToken", _login?.data?.refreshToken);
        storeValue("id", _login?.data?.id);
        secureLocalStorage.setItem("partnerName", _login?.data?.name)

        const verifiedBool = !!_login?.data?.verified;
        const pricingBool = !!_login?.data?.pricing;

        // keep your current behavior
        localStorage.setItem("role", "hospital");
        localStorage.setItem("partnerVerified", verifiedBool ? "1" : "0");
        localStorage.setItem("pricingDone", pricingBool ? "1" : "0");

        secureLocalStorage.setItem("role", "hospital");
        secureLocalStorage.setItem("partnerVerified", verifiedBool);
        secureLocalStorage.setItem("pricingDone", pricingBool);


        secureLocalStorage.setItem("loginEmail", cleanEmail);
        secureLocalStorage.setItem("isAuth", true);
        secureLocalStorage.setItem("loginTime", Date.now());

        window.dispatchEvent(new Event("authUpdate"));

        if (!verifiedBool) {
          navigate("/partnerProfile");
        } else if (verifiedBool && !pricingBool) {
          navigate("/partnerProfile");
        } else {
          navigate("/partner/careteam");
        }
      } else {
        showToast(_login?.message || "Invalid OTP. Please try again.", "Error");
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      showToast("Failed to verify OTP. Please try again.", "Error");
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  const ResendOtp = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!isValidEmail(cleanEmail)) {
      showToast("Enter a valid email address", "Error");
      return;
    }

    setResendOtpLoading(true);
    try {
      const res = await HospitalOTP({ email: cleanEmail });
      if (res?.error === false) {
        showToast("OTP resent successfully.", "Success");
        setTimer(60);
        setShowResend(false);
        setOtp("");
      } else {
        showToast(res?.message || "Failed to resend OTP", "Error");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      showToast("Failed to resend OTP. Please try again.", "Error");
    } finally {
      setResendOtpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row">
      {/* Left image section */}
      <div className="w-full lg:w-1/2 h-52 sm:h-60 md:h-72 lg:h-auto">
        <div
          className="h-full w-full bg-cover bg-center"
          style={{ backgroundImage: `url(${LoginImages.cover})` }}
        />
      </div>

      {/* Right form section */}
      <div className="w-full lg:w-1/2 flex items-center justify-center py-8 sm:py-10 lg:py-0
                      px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-24">
        {/* Wrapper to keep same UI width on all screens */}
        <div className="w-full flex flex-col items-center">
          <img
            src={LoginImages.logo}
            alt="Logo"
            className="h-10 sm:h-11 w-[160px] sm:w-[180px] mb-6"
          />

          {/* CARD width controlled per breakpoint */}
          <div
            className="
              w-full
              max-w-[360px] sm:max-w-[400px] md:max-w-[440px]
              lg:max-w-[420px] xl:max-w-[460px] 2xl:max-w-[520px]
            "
          >
            {!isOtpScreen ? (
              <div className="w-full bg-white shadow-lg rounded-2xl
                              px-5 sm:px-6 md:px-8 xl:px-10
                              py-7 sm:py-8 md:py-10
                              space-y-6">
                <div>
                  <h2 className="text-[20px] font-semibold mb-1 font-poppins">
                    Sign In
                  </h2>
                  <p className="text-[12px] text-gray-600 font-poppins">
                    Get an OTP on your registered email ID
                  </p>
                </div>

                <div className="space-y-2 pt-2">
                  <label className="text-[13px] text-[#606060] font-poppins block">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full border border-[#D2D2D2] px-3 py-2 rounded-md text-[14px]
                               font-poppins focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                  />
                </div>

                <button
                  onClick={sendOTP}
                  disabled={sendOtpLoading}
                  className={`bg-custom-gradient w-full rounded-3xl py-2.5 text-white text-sm
                              font-poppins transition ${sendOtpLoading
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:opacity-90"
                    }`}
                >
                  {sendOtpLoading ? "Sending..." : "Send OTP"}
                </button>

                <div className="flex justify-center pt-2">
                  <Link
                    to="/#home"
                    className="text-blue-600 underline text-[14px] font-poppins hover:text-blue-700"
                  >
                    Go back
                  </Link>
                </div>
              </div>
            ) : (
              <div className="w-full bg-white shadow-lg rounded-2xl
                              px-5 sm:px-6 md:px-8 xl:px-10
                              py-7 sm:py-8 md:py-10
                              space-y-6">
                <div>
                  <h2 className="text-[18px] md:text-[20px] font-semibold font-poppins mb-1">
                    Enter OTP
                  </h2>
                  <p className="text-[12px] md:text-[13px] text-gray-600 font-poppins">
                    We’ve sent a 4-digit OTP to{" "}
                    <span className="font-semibold text-gray-800">
                      {maskEmail(email)}
                    </span>
                  </p>
                </div>

                <input
                  type="text"
                  maxLength={4}
                  value={otp}
                  onChange={handleOtpChange}
                  className="w-full text-center text-[16px] tracking-[0.4em]
                             border border-gray-300 rounded-md px-4 py-2 font-poppins
                             focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="• • • •"
                />

                <button
                  onClick={verifyOTP}
                  disabled={verifyOtpLoading}
                  className={`bg-custom-gradient rounded-3xl py-2.5 w-full text-white text-sm
                              font-poppins transition ${verifyOtpLoading
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:opacity-90"
                    }`}
                >
                  {verifyOtpLoading ? "Logging in..." : "Log In"}
                </button>

                <div className="flex justify-between items-center text-[12px] font-poppins">
                  {showResend ? (
                    <button
                      type="button"
                      onClick={ResendOtp}
                      disabled={resendOtpLoading}
                      className={`text-blue-600 underline ${resendOtpLoading
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:text-blue-700"
                        }`}
                    >
                      {resendOtpLoading ? "Resending..." : "Resend OTP"}
                    </button>
                  ) : (
                    <p className="text-gray-500">
                      Resend OTP in <span>{timer}s</span>
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setIsOtpScreen(false);
                      setOtp("");
                      setTimer(60);
                      setShowResend(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 underline"
                  >
                    Change email
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* end card width wrapper */}
        </div>
      </div>
    </div>
  );
}

export default HospitalLogin;